import { useFetchCallType } from '@rahtab/auth/lib/token-based-auth';
import { useAppDispatch, useSelector } from 'rahtab-redux';
import { addListing, listingSelector } from 'rahtab-redux/slices/listingsSlice';
import { useState } from 'react';
import { OrderingType } from './useMerchandises';

function useListings({
  deliveryDate,
  merchandiseId,
  orderingType,
  termCode,
}: {
  deliveryDate: string;
  merchandiseId: string;
  orderingType: OrderingType;
  termCode: string;
}) {
  const [idList, setIdList] = useState<Set<string> | undefined>();
  const dispatch = useAppDispatch();
  const listings = useSelector(listingSelector(Array.from(idList?.values() || [])));
  const listingsLoading = useSelector((state) => state.listings.loading);
  const [_, { data: termList = [], loading: termListLoading }] = useFetchCallType<GetTemplateTermListResult>(
    'GetTemplateTermList',
    {
      params: `TemplateCode=${merchandiseId}`,
      skip: orderingType === OrderingType.DAILY,
    },
  );
  const [refetch, { data, loading, called }] = useFetchCallType<GetTemplateOfferListResult>('GetTemplateOfferList', {
    skip: orderingType === OrderingType.PERIODIC && !termCode,
    params: `OrderingType=${orderingType}&TemplateCode=${merchandiseId}&DeliveryDate=${
      orderingType === OrderingType.DAILY ? deliveryDate : ''
    }&TermCode=${termCode}`,
    onCompleted: (d) => {
      let temp = new Set<string>();
      d.data.forEach((lstng) => {
        temp.add(lstng.OfferCode);
        dispatch(
          addListing({
            ...lstng,
            maxOrderingValue: d.maxOrderingValue,
            TemplateCode: merchandiseId,
            sumOfferMeasureName: d.sumOfferMeasureName,
            termName: termList.find(({ TermCode }) => TermCode === termCode)?.TermName,
          }),
        );
      });
      setIdList(temp);
    },
  });

  return {
    data: {
      listings,
      viewerOrderLimit: data?.maxOrderingValue,
      viewerOrderRemainingQuantity: data?.remOrderingValue,
      viewerTotalOrderQuantity: data?.sumOrderingValue,
      sellerCount: data?.supplierCount,
      merchandiseUnitText: data?.sumOfferMeasureName,
      termList,
    },
    listingsLoading,
    loading: termListLoading || loading,
    called,
    refetch,
  };
}

export default useListings;

type GetTemplateTermListResult = {
  TermCode: string;
  TermName: string;
  StartDateShmasi: string;
  EndDateShamsi: string;
}[];

export interface OfferInfo {
  row: string;
  OfferCode: string;
  EnOfferCode: string;
  /** موجودی عرضه قابل سفارش گذاری */
  RemainingValue: string;
  /** حداکثر مقدار سفارش گذاری به تفکیک سه رقم */
  RemainingValueStr: string;
  /** واحد مقدار */
  MeasureName: string;
  MeasureCode: string;
  /** قیمت فروش  */
  PricePerUnit: string;
  /** یمت فروش به تفکیک سه رقم */
  PricePerUnitStr: string;
  CheckoutType: string;
  OfferDate: string;
  TemplateName: string;
  /** شناسه فروشنده */
  SupplierIdentification: string;
  /** نام فروشنده */
  SupplierName: string;
  SupplierImage: string;
  SupplierPhone: string;
  OtherSupplierName: string;
  ItemWeight: string;
  DeliveryDate: string;
  AvailableItemCount: string;
  AvailableItemCountStr: string;
  /** محل تحویل */
  DeliveryLocation: string;
  /** تعداد نظر */
  CommentCount: string;
  /** مقدار قابل سفارش گذاری */
  ClientAvailableAmount: string;
  HasPriority: boolean;
}

export interface GetTemplateOfferListResult {
  /** لیست عرضه ها جهت سفارش گذاری */
  data: OfferInfo[];
  /** کل مقدار قابل سفارش تمامی عرضه‌ها*/
  sumOfferAmount: string;
  /** مقدار قابل سفارش‌گذاری برای این کاربر */
  maxOrderingValue: string;
  /** مقدار قابل سفارش‌گذاری باقیمانده برای این کاربر */
  remOrderingValue: string;
  /** واحد مقدار */
  sumOfferMeasureName: string;
  /** تعداد فروشندگان */
  supplierCount: string;
  /** مقدار سفارش گذاری شده */
  sumOrderingValue: string;
}
